import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CATS = ["marketing", "utility", "authentication", "service"];
export const gbp = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };

function RateCard({ canEdit }) {
  const [card, setCard] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/wa/rate-card").then((r) => setCard(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/wa/rate-card", { ...edit, rates: Object.fromEntries(CATS.map((c) => [c, Number(edit.rates[c])])) }); toast.success("Rate card saved"); setEdit(null); load(); }
    catch (e) { toast.error(err(e)); }
  };
  if (!card) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="wa-rate-card">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold text-slate-900">Meta rate card (UK, per delivered message, ex VAT)</h2>
        {canEdit && !edit && <Button size="sm" variant="outline" onClick={() => setEdit({ effective_from: card.effective_from, rates: { ...card.rates }, source: "", vat_rate: card.vat_rate })} data-testid="wa-rate-edit">Update rates</Button>}
      </div>
      <p className="text-xs text-slate-500 mb-3">From {card.effective_from} · {card.source}{card.is_default ? " · default" : ""}{card.free_service_per_month ? ` · first ${card.free_service_per_month.toLocaleString()} service messages per number per month free` : ""}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {CATS.map((c) => (
          <div key={c} className="rounded-lg border border-slate-100 p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">{c}</div>
            {edit ? <Input type="number" step="0.0001" value={edit.rates[c]} onChange={(e) => setEdit({ ...edit, rates: { ...edit.rates, [c]: e.target.value } })} data-testid={`wa-rate-input-${c}`} />
              : <div className="text-base font-bold text-slate-900" data-testid={`wa-rate-${c}`}>{gbp(card.rates[c])}</div>}
          </div>
        ))}
      </div>
      {edit && (
        <div className="mt-3 flex flex-wrap gap-2 items-end">
          <label className="text-xs text-slate-500">Effective from<Input type="date" value={edit.effective_from} onChange={(e) => setEdit({ ...edit, effective_from: e.target.value })} data-testid="wa-rate-effective" /></label>
          <label className="text-xs text-slate-500 flex-1 min-w-[200px]">Source<Input value={edit.source} placeholder="e.g. Meta rate card Jan 2027" onChange={(e) => setEdit({ ...edit, source: e.target.value })} data-testid="wa-rate-source" /></label>
          <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="wa-rate-save">Save</Button>
          <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
        </div>
      )}
    </section>
  );
}

function Estimator() {
  const [f, setF] = useState({ recipients: 2000, campaigns: 4, service_replies: 1500, utility_messages: 500 });
  const [r, setR] = useState(null);
  const run = async () => {
    try { setR((await api.post("/wa/costs/estimate", { recipients: f.recipients * f.campaigns, category: "marketing", service_replies: f.service_replies, utility_messages: f.utility_messages })).data); }
    catch (e) { toast.error(err(e)); }
  };
  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const field = (k, label) => <label className="text-xs text-slate-500">{label}<Input type="number" min="0" value={f[k]} onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })} data-testid={`wa-est-${k}`} /></label>;
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="wa-estimator">
      <h2 className="text-sm font-bold text-slate-900 mb-1">Monthly cost estimate</h2>
      <p className="text-xs text-amber-700 mb-3" data-testid="wa-est-disclaimer">Estimate only, not a guaranteed charge. Meta bills per delivered message and may change rates.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{field("recipients", "Recipients per campaign")}{field("campaigns", "Campaigns per month")}{field("service_replies", "Service replies")}{field("utility_messages", "Utility messages")}</div>
      <Button variant="outline" className="mt-3" onClick={run} data-testid="wa-est-run">Calculate</Button>
      {r && (
        <div className="mt-3 text-sm" data-testid="wa-est-result">
          {r.lines.map((l) => <div key={l.category} className="flex justify-between border-b border-slate-100 py-1"><span className="capitalize text-slate-600">{l.category} · {l.count.toLocaleString()}{l.free ? ` (${l.free.toLocaleString()} free)` : ""} × {gbp(l.rate)}</span><span>{gbp(l.amount)}</span></div>)}
          <div className="flex justify-between py-1 font-bold"><span>Total ex VAT</span><span data-testid="wa-est-total">{gbp(r.total.ex_vat)}</span></div>
          <div className="flex justify-between text-xs text-slate-500"><span>Inc VAT</span><span>{gbp(r.total.inc_vat)}</span></div>
        </div>
      )}
    </section>
  );
}

function ActualReport() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/wa/costs/report").then((x) => setR(x.data)).catch(() => {}); }, []);
  if (!r) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="wa-cost-report">
      <h2 className="text-sm font-bold text-slate-900 mb-1">Actual charges · {r.month}</h2>
      <p className="text-xs text-slate-500 mb-3">{r.source}</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
        {CATS.map((c) => <div key={c}><div className="text-[11px] uppercase text-slate-400">{c}</div><b>{gbp(r.by_category[c].amount)}</b> <span className="text-xs text-slate-400">({r.by_category[c].count})</span></div>)}
        <div><div className="text-[11px] uppercase text-slate-400">Total ex VAT</div><b data-testid="wa-cost-total">{gbp(r.total.ex_vat)}</b></div>
      </div>
    </section>
  );
}

export function WaCostsTab({ canEdit }) {
  return <div className="space-y-5"><RateCard canEdit={canEdit} /><Estimator /><ActualReport /></div>;
}
